import { AddCircleOutline, Close, VerifiedUser } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BaseOperation,
  OPERATION_TYPES,
  OperationAssociationValue,
} from 'pages/BusinessProfile/Contracts/Portfolios/Components/OperationAssociation/operations';
import React, { useMemo, useState } from 'react';

import OperationsDialog from './OperationsDialog';

interface OperationAssociationProps {
  value: OperationAssociationValue;
  onChange: (value: OperationAssociationValue) => void;
  businessIdentifier: string | undefined;
}

const OperationAssociation: React.FC<OperationAssociationProps> = ({
  value,
  onChange,
  businessIdentifier,
}) => {
  const [openDialog, setOpenDialog] = useState(false);

  const operationLabel = useMemo(() => {
    if (!value.operation || !value.operationType) return '';
    const config = OPERATION_TYPES[value.operationType];
    return config ? config.getLabel(value.operation) : '';
  }, [value.operation, value.operationType]);

  const handleToggleEnabled = (checked: boolean) => {
    if (!checked) {
      onChange({
        enabled: false,
        operationType: undefined,
        operation: undefined,
      });
      return;
    }
    onChange({
      enabled: true,
      operationType: 'warranty-request', // Default to warranty-request
      operation: undefined,
    });
  };

  const handleOperationTypeChange = (event: SelectChangeEvent<string>) => {
    const newType = event.target.value;
    onChange({
      ...value,
      operationType: newType,
      operation: undefined,
    });
  };

  const handleSelectOperation = (operation: BaseOperation) => {
    onChange({
      ...value,
      operation: operation,
    });
  };

  const handleRemoveOperation = () => {
    onChange({
      ...value,
      operation: undefined,
    });
  };

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      {value.enabled && (
        <>
          <Box display="flex" alignItems="center" gap={1}>
            <FormControl fullWidth margin="dense">
              <InputLabel id="operation-type-label">
                Tipo de operación
              </InputLabel>
              <Select
                labelId="operation-type-label"
                id="operation-type-select"
                value={value.operationType ?? ''}
                label="Tipo de operación"
                onChange={handleOperationTypeChange}
              >
                {Object.values(OPERATION_TYPES).map((config) => (
                  <MenuItem key={config.key} value={config.key}>
                    {config.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Seleccionar Operación">
              <span>
                <IconButton
                  size="medium"
                  onClick={() => setOpenDialog(true)}
                  disabled={!value.operationType}
                >
                  <AddCircleOutline />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {value.operation && (
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                borderColor: '#E5E7EB',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(15, 35, 223, 0.08)',
                      borderRadius: '6px',
                      p: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <VerifiedUser
                      sx={{ fontSize: 18, color: 'primary.main' }}
                    />
                  </Box>
                  <Typography
                    variant="subtitle2"
                    noWrap
                    title={value.operation?.id?.toString()}
                    sx={{
                      fontWeight: 600,
                      maxWidth: { xs: '40vw', md: '50vw' },
                    }}
                  >
                    {operationLabel}
                  </Typography>
                </Box>
                <IconButton
                  aria-label="remove-operation"
                  onClick={handleRemoveOperation}
                  size="small"
                >
                  <Close />
                </IconButton>
              </Box>
            </Paper>
          )}
          <OperationsDialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            operationType={value.operationType ?? ''}
            onSelectOperation={handleSelectOperation}
            businessIdentifier={businessIdentifier}
          />
        </>
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={value.enabled}
            onChange={(e) => handleToggleEnabled(e.target.checked)}
          />
        }
        label={<Typography variant="body2">Asociar a una operación</Typography>}
      />
    </Box>
  );
};

export default OperationAssociation;
